import {OccupationBureauDto} from './occupation-bureau.dto';
import {Duree} from '../../shared/models/duree.model';
import {BureauDto} from '../../shared/dto/bureau.dto';
import {isNullOrUndefined} from 'sigale-angular';
import {TranslateService} from '@ngx-translate/core';

export class Bureau {
	id: number;
	nom: string;
	coordonneeX: number;
	coordonneeY: number;
	occupationsBureaux: OccupationBureauDto[];

	private translateService: TranslateService;

	constructor(translateService: TranslateService, bureauDto: BureauDto, coordonneeX: number, coordonneeY: number) {
		this.translateService = translateService;
		this.id = bureauDto.id;
		this.nom = bureauDto.nom;
		this.coordonneeX = coordonneeX;
		this.coordonneeY = coordonneeY;
		this.occupationsBureaux = bureauDto.occupationsBureaux.map(occupationBureauDto => new OccupationBureauDto().deserialize(occupationBureauDto));
	}

	estReserveDansLaJournee(): boolean {
		return this.estReserveSurLaJournee() || this.reserverSeulementMatin() || this.reserverSeulementApresMidi();
	}

	possedeDifferentsOccupantsSurJournee(): boolean {
		const occupationBureauMatin = this.occupationsBureaux.find(occupation => occupation.duree.code === Duree.MATIN);
		const occupationBureauApresMidi = this.occupationsBureaux.find(occupation => occupation.duree.code === Duree.APRES_MIDI);

		if (occupationBureauMatin && occupationBureauApresMidi) {
			return occupationBureauMatin.nomOccupant !== occupationBureauApresMidi.nomOccupant || occupationBureauMatin.prenomOccupant !== occupationBureauApresMidi.prenomOccupant;
		}

		return false;
	}

	estUtilisateurCourantOccupantBureauMatinee(login: string): boolean {
		const occupationBureauMatin = this.occupationsBureaux.find(occupation => occupation.duree.code === Duree.MATIN);

		if (occupationBureauMatin) {
			return occupationBureauMatin.loginOccupant === login;
		}

		return false;
	}

	estUtilisateurCourantOccupantBureauApresMidi(login: string): boolean {
		const occupationBureauApresMidi = this.occupationsBureaux.find(occupation => occupation.duree.code === Duree.APRES_MIDI);

		if (occupationBureauApresMidi) {
			return occupationBureauApresMidi.loginOccupant === login;
		}

		return false;
	}

	estUtilisateurOccupantBureauSurLaJournee(login: string): boolean {
		return this.occupationsBureaux.find(occupationBureau =>
			occupationBureau.loginOccupant === login) !== undefined;
	}

	prenomsNomsReservant(): string {
		// S'il y a un conflit alors, on ne veut pas afficher le nom de la personne avec le bureau préférentiel.
		const occupationsBureaux = this.estReservationEnConflit() ? this.recupererOccupationsConflitSansUtilisateurAvecBureauPreferentiel() : this.occupationsBureaux;

		if (occupationsBureaux.length <= 0) {
			return '';
		}

		if (occupationsBureaux.length === 1) {
			const prenom = isNullOrUndefined(occupationsBureaux[0]) ? '' : occupationsBureaux[0].prenomOccupant;
			const nom = isNullOrUndefined(occupationsBureaux[0]) ? '' : occupationsBureaux[0].nomOccupant;

			return prenom + ' ' + nom;
		}

		return this.recupererPrenomsPlusieursReservants(occupationsBureaux);
	}

	prenomsReservant(): string {
		// S'il y a un conflit alors, on ne veut pas afficher le nom de la personne avec le bureau préférentiel.
		const occupationsBureaux = this.estReservationEnConflit() ? this.recupererOccupationsConflitSansUtilisateurAvecBureauPreferentiel() : this.occupationsBureaux;

		if (occupationsBureaux.length <= 0) {
			return '';
		}

		if (occupationsBureaux.length === 1) {
			return isNullOrUndefined(occupationsBureaux[0]) ? '' : occupationsBureaux[0].prenomOccupant;
		}

		return this.recupererPrenomsPlusieursReservants(occupationsBureaux);
	}

	private recupererPrenomsPlusieursReservants(occupations: OccupationBureauDto[]): string {
		if (occupations.length > 1) {
			const prenomMatin = isNullOrUndefined(this.recupererOccupationBureauMatin()[0]) ? '' : this.recupererOccupationBureauMatin()[0].prenomOccupant;
			const prenomApresMidi = isNullOrUndefined(this.recupererOccupationBureauApresMidi()[0]) ? '' : this.recupererOccupationBureauApresMidi()[0].prenomOccupant;

			return prenomMatin + ' / ' + prenomApresMidi;
		}

		return '';
	}

	private recupererOccupationsConflitSansUtilisateurAvecBureauPreferentiel(): OccupationBureauDto[] {
		if (this.recupererOccupationBureauJournee().length > 1) {
			return this.occupationsBureaux.filter(occupation => !occupation.estUtilisateurPreferentiel);
		}

		if (this.recupererOccupationBureauMatin().length > 1) {
			return this.occupationsBureaux.filter(occupation => occupation.duree.code !== Duree.MATIN || !occupation.estUtilisateurPreferentiel);
		}

		if (this.recupererOccupationBureauApresMidi().length > 1) {
			return this.occupationsBureaux.filter(occupation => occupation.duree.code !== Duree.APRES_MIDI || !occupation.estUtilisateurPreferentiel);
		}

		if (this.recupererOccupationBureauJournee().length === 1 && this.recupererOccupationBureauMatin().length > 0) {
			return this.occupationsBureaux.filter(occupation => occupation.duree.code === Duree.APRES_MIDI
				|| (occupation.duree.code === Duree.JOURNEE) && !occupation.estUtilisateurPreferentiel
				|| (occupation.duree.code === Duree.MATIN) && !occupation.estUtilisateurPreferentiel);
		}

		if (this.recupererOccupationBureauJournee().length === 1 && this.recupererOccupationBureauApresMidi().length > 0) {
			return this.occupationsBureaux.filter(occupation => occupation.duree.code === Duree.MATIN
				|| (occupation.duree.code === Duree.JOURNEE) && !occupation.estUtilisateurPreferentiel
				|| (occupation.duree.code === Duree.APRES_MIDI) && !occupation.estUtilisateurPreferentiel);
		}

		return this.occupationsBureaux;
	}

	estReservationEnConflit(): boolean {
		return this.recupererOccupationBureauJournee().length > 1 ||

			// conflit car 2 personnes avec réservation matin
			this.recupererOccupationBureauMatin().length > 1 ||

			// conflit car 2 personnes avec réservation après-midi
			this.recupererOccupationBureauApresMidi().length > 1 ||

			// conflit car 1 personne avec réservation journée et 1 personnes le matin
			this.recupererOccupationBureauJournee().length === 1 && this.recupererOccupationBureauMatin().length > 0 ||

			// conflit car 1 personne avec réservation journée et 1 personne l'après-midi
			this.recupererOccupationBureauJournee().length === 1 && this.recupererOccupationBureauApresMidi().length > 0;
	}

	/**
	 * Le bureau est considéré comme réservé pour la journée s'il a une réservation la journée OU une le matin ET une l'après-midi.
	 */
	estReserveSurLaJournee(): boolean {
		return this.possedeUneDureeReservationJournee() || (this.possedeUneDureeReservationMatin() && this.possedeUneDureeReservationApresMidi());
	}

	reserverSeulementMatin(): boolean {
		return this.possedeUneDureeReservationMatin() && !this.possedeUneDureeReservationApresMidi();
	}

	reserverSeulementApresMidi(): boolean {
		return this.possedeUneDureeReservationApresMidi() && !this.possedeUneDureeReservationMatin();
	}

	aucuneOccupation(): boolean {
		return this.occupationsBureaux.length === 0;
	}

	private recupererOccupationBureauJournee(): OccupationBureauDto[] {
		return this.occupationsBureaux.filter(occupationBureau => occupationBureau.duree.code === Duree.JOURNEE);
	}

	private recupererOccupationBureauMatin(): OccupationBureauDto[] {
		return this.occupationsBureaux.filter(occupationBureau => occupationBureau.duree.code === Duree.MATIN);
	}

	private recupererOccupationBureauApresMidi(): OccupationBureauDto[] {
		return this.occupationsBureaux.filter(occupationBureau => occupationBureau.duree.code === Duree.APRES_MIDI);
	}

	private possedeUneDureeReservationJournee(): boolean {
		return !this.aucuneOccupation() && this.recupererOccupationBureauJournee().length > 0;
	}

	private possedeUneDureeReservationMatin(): boolean {
		return !this.aucuneOccupation() && this.recupererOccupationBureauMatin().length > 0;
	}

	private possedeUneDureeReservationApresMidi(): boolean {
		return !this.aucuneOccupation() && this.recupererOccupationBureauApresMidi().length > 0;
	}

}
